import { CartResponseSchema } from '../schemas/cartSchemas';
import type { CartData, RawCartData, CartItem } from '../types/cartTypes';

/**
 * 将原始的 `/cart.js` 响应数据映射为领域模型。
 * - 使用 Zod 进行运行时校验和默认值填充。
 * - 转换字段名（如 `item_count` 映射为 `count`）。
 * @param raw - 原始响应数据 (来自 Shopify Ajax API)
 * @returns 映射后的 CartData 对象
 */
export function mapCartResponse(raw: RawCartData | unknown): CartData {

  // 1. Zod Validation (replaces manual checks)
  // safeParse might be better if we want to swallow errors, but User Plan says "explicitly throw ZodErrors" or at least we should handle it.
  // The plan says: "If validation fails, fallback to a safe constant or log a critical error (depending on severity). Ideally, use safeParse and log warning on partial failures."
  // However, CartResponseSchema uses .default() heavily, so it shouldn't fail easily unless the structure is fundamentally wrong (not an object).

  const result = CartResponseSchema.safeParse(raw);

  if (!result.success) {
    console.error('[cartData] 🚨 Zod Validation Failed:', result.error);
    // Fallback? or throw?
    // Given the critical nature, we might return a safe empty cart to prevent white screen,
    // but re-throwing might be safer to trigger Error Boundary if the API is completely broken.
    // For now, let's return a safe empty cart but log aggressively.
    return {
      count: 0,
      item_count: 0,
      currency: 'EUR',
      items: [],
      cart_level_discount_applications: [],
      discount_codes: [],
      total_discount: 0,
      original_total_price: 0,
      total_price: 0,
    };
  }

  const parsed = result.data;
  console.log('[cartData] discount applications', parsed.cart_level_discount_applications);

  const itemsWithStableId = parsed.items.map((item) => {
    if (item.properties?.__clientLineId) return item;
    const fallbackId = buildFallbackLineId(item);
    return {
      ...item,
      properties: {
        ...(item.properties ?? {}),
        __clientLineId: fallbackId
      }
    };
  });

  const discountCodes = (parsed.discount_codes ?? [])
    .map((entry) => ({
      code: entry.code?.trim() ?? '',
      applicable: Boolean(entry.applicable)
    }))
    .filter((entry) => entry.code && entry.code !== '0');

  const applicableDiscountCodes = discountCodes
    .filter((entry) => entry.applicable)
    .map((entry) => entry.code);

  const cartLevelDiscounts = parsed.cart_level_discount_applications.length > 0
    ? parsed.cart_level_discount_applications.map((discount) => {
      if (discount.type !== 'discount_code') return discount;
      const title = discount.title?.trim();
      if (title) return discount;
      const fallbackCode = applicableDiscountCodes[0];
      return fallbackCode
        ? { ...discount, title: fallbackCode }
        : discount;
    })
    : (applicableDiscountCodes.length === 1 && parsed.total_discount > 0)
      ? [{
        type: 'discount_code',
        title: applicableDiscountCodes[0],
        total_allocated_amount: parsed.total_discount
      }]
      : [];

  // 2. Map to Domain Model
  return {
    count: parsed.item_count,
    item_count: parsed.item_count,
    currency: parsed.currency,
    items: itemsWithStableId, // Zod already normalized the items
    cart_level_discount_applications: cartLevelDiscounts,
    discount_codes: discountCodes,
    total_discount: parsed.total_discount,
    original_total_price: parsed.original_total_price,
    total_price: parsed.total_price,
  };
}

function buildFallbackLineId(item: CartItem): string {
  const sellingPlanId = item.selling_plan_allocation?.selling_plan_id ?? '';
  const stableProps = normalizeProperties(item.properties);
  const identity = `${item.id}|${sellingPlanId}|${stableProps}`;
  return `fallback_${hashString(identity)}`;
}

function normalizeProperties(props?: Record<string, string>): string {
  if (!props) return '';
  const entries = Object.entries(props).filter(([key]) => !key.startsWith('__'));
  if (entries.length === 0) return '';
  entries.sort(([a], [b]) => a.localeCompare(b));
  return entries.map(([key, value]) => `${key}:${value}`).join('|');
}

function hashString(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) + hash) + input.charCodeAt(i);
    hash >>>= 0;
  }
  return hash.toString(36);
}
